import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../config';
import CustomerLink from '../../../Components/CustomerLink';
import ImdMoneySection from './ImdMoneySection';
import BagEntriesPanel from './BagEntriesPanel';
import { asArray, buildSalesSummaryRows, getBagSalesData, getItemsData, getSection, productLabel, renderTable } from './saleReportHelpers';

// Same classification convention as PaymentCodeReferenceSeeder (saledata-module) and
// DriverDailyReport.js's buildSummary (vector 2026-07-21: paymentCode was showing raw, e.g. "BT"/
// "CASH-500", instead of a readable name — reusing DriverDailyReport.js's exact logic here instead
// of introducing a separate reference-table join). Legacy codes (M-1/M-20/M-50/DC) predate this
// convention and fall through to the raw code, same as DriverDailyReport.js's own aggregation.
const CASH_PREFIX = 'CASH-';
const BANK_TRANSFER_CODE = 'BT';
const FUEL_CODE = 'FUEL';
const COIN_MAX_VALUE = 10;

const paymentCodeLabel = (code) => {
    if (!code) return '-';
    if (code === FUEL_CODE) return 'ค่าน้ำมัน';
    if (code === BANK_TRANSFER_CODE) return 'ยอดโอน';
    if (code.startsWith(CASH_PREFIX)) {
        const value = Number(code.slice(CASH_PREFIX.length)) || 0;
        if (value > 0 && value <= COIN_MAX_VALUE) return 'เหรียญ';
        if (value > 0) return `${value.toLocaleString()} บาท`;
    }
    return code;
};

// createdAt arrives as an ISO datetime string (e.g. "2026-09-14T09:23:33.004107") — just the
// clock time is wanted here, not the date (already shown elsewhere on this page).
const formatHHmm = (v) => {
    if (!v) return '';
    const t = String(v).includes('T') ? String(v).split('T')[1] : String(v);
    return t.slice(0, 5);
};

const renderMasterDetails = (master) => {
    if (!master) return null;
    return (
        <div className="btc-section">
            <h3>รายละเอียดสรุป </h3>
            <div className="btc-grid2">
                <p><strong>รหัสรายงาน:</strong> {master.reportCode}</p>
                <p><strong>รอบ:</strong> {master.round}</p>
                <p><strong>เติมน้ำแข็งรวม:</strong> {(master.totalRefill ?? master.totalMeltCompensation)}</p>
                <p><strong>เริ่มส่ง:</strong> {master.firstDelivery}</p>
                <p><strong>ส่งสุดท้าย:</strong> {master.lastDelivery}</p>
                <p><strong>ส่งเอกสาร:</strong> {master.submit ? 'ใช่' : 'ไม่'}</p>
                <p><strong>ตรวจสอบแล้ว:</strong> {master.verified ? 'ใช่' : 'ไม่'}</p>
                <p><strong>ส่งของแล้ว:</strong> {master.delivered ? 'ใช่' : 'ไม่'}</p>
            </div>
        </div>
    );
};

// สรุปรายงาน — bundles the already-titled summary tables (phase 2 UI cleanup gave this
// tab its own umbrella label; the individual table titles below are unchanged).
// ลงกระสอบกลับจากหน้า report (Vector 2026-09-26: "ให้มีหลายทาง imd ส่งเงินก็มี และ หน้า report ด้วย").
// The server takes route / business date / กะ from the round's report code — only the counts are typed.
const BagReturnSection = ({ fullReport, onReload }) => {
    const bagReturns = asArray(getSection(fullReport, ['bagReturnForRound'], []));
    const reportCode = fullReport?.masterReport?.reportCode;
    const current = bagReturns[bagReturns.length - 1];
    const [tube, setTube] = useState(current?.tubeQty ?? '');
    const [crushed, setCrushed] = useState(current?.crushedQty ?? '');
    const [note, setNote] = useState(current?.note ?? '');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        setTube(current?.tubeQty ?? '');
        setCrushed(current?.crushedQty ?? '');
        setNote(current?.note ?? '');
    }, [current?.id, current?.tubeQty, current?.crushedQty, current?.note]);

    const save = async () => {
        if (!reportCode) return;
        setSaving(true);
        setMessage('');
        try {
            const token = JSON.parse(localStorage.getItem('user') || 'null')?.token;
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/bag-return/for-report/${encodeURIComponent(reportCode)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    returnedTubeQty: tube === '' ? 0 : Number(tube),
                    returnedCrushedQty: crushed === '' ? 0 : Number(crushed),
                    note,
                }),
            });
            if (!res.ok) {
                setMessage('บันทึกไม่สำเร็จ: ' + ((await res.text().catch(() => '')) || res.status));
                return;
            }
            setMessage(current ? 'แก้ไขกระสอบกลับแล้ว' : 'บันทึกกระสอบกลับแล้ว');
            if (onReload) await onReload();
        } catch (e) {
            setMessage('บันทึกไม่สำเร็จ: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    const inputStyle = { width: 90, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' };
    // One wrapper element: the page lays sections out in a 2-column grid, so a fragment would put
    // the form in its own cell beside the table instead of under it.
    return (
        <div>
            {renderTable(
                'กระสอบกลับ',
                bagReturns,
                [
                    { key: 'tubeQty', label: 'หลอดใหญ่' },
                    { key: 'crushedQty', label: 'บด' },
                ],
                'ไม่มีข้อมูล'
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '6px 0 16px' }}>
                <strong>{current ? 'แก้กระสอบกลับ' : 'ลงกระสอบกลับ'}:</strong>
                <span>หลอดใหญ่</span>
                <input type="number" min="0" value={tube} onChange={e => setTube(e.target.value)} style={inputStyle} />
                <span>บด</span>
                <input type="number" min="0" value={crushed} onChange={e => setCrushed(e.target.value)} style={inputStyle} />
                <input type="text" placeholder="หมายเหตุ" value={note} onChange={e => setNote(e.target.value)} style={{ ...inputStyle, width: 180 }} />
                <button className="btc-btn btn-sm" style={{ backgroundColor: '#343a40' }} onClick={save} disabled={saving || !reportCode}>
                    {saving ? 'กำลังบันทึก...' : 'บันทึก (จบกะ)'}
                </button>
                {message && <span style={{ color: message.startsWith('บันทึกไม่สำเร็จ') ? '#c62828' : '#2e7d32' }}>{message}</span>}
            </div>
        </div>
    );
};

// รายการกระสอบของรอบ — เปิด/ปิดแผงแก้ไข (Vector 2026-10-28: "เห็นเป็นรายการว่ากรอกมาแล้วกี่ครั้ง แก้ไข ลบ เพิ่มได้")
const BagEntriesSection = ({ reportCode, onReload }) => {
    const [open, setOpen] = useState(false);
    return (
        <div style={{ marginTop: 8 }}>
            <button className="btc-btn btn-sm" style={{ backgroundColor: '#343a40' }} onClick={() => setOpen(!open)}>
                {open ? 'ซ่อนรายการกระสอบ' : 'จัดการรายการกระสอบ'}
            </button>
            {open && <BagEntriesPanel reportCode={reportCode} onChanged={onReload} />}
        </div>
    );
};

// เงินค้างเก่าที่คนขับเก็บได้ในรอบนี้ (IMD รับชำระ) — Vector 2026-10-07 tg_11734 ข้อ ข: นับเข้า "ต้องส่ง" ของรอบที่เก็บ
// (สูตรเดียวกับการ์ดสรุป IMD/ตรวจเงิน) · ไม่มี = ไม่แสดง · Best-effort: ล้มแล้วไม่แสดงอะไร ไม่ทำให้รายงานพัง
const CollectedDebtSection = ({ reportCode }) => {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        let cancelled = false;
        setRows([]);
        (async () => {
            try {
                const token = JSON.parse(localStorage.getItem('user') || 'null')?.token;
                if (!token || !reportCode) return;
                const res = await fetch(`${API_BASE_URL}/saledata/api/module/credit-debts/collected-in-round?reportCode=${encodeURIComponent(reportCode)}`, {
                    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`collected-in-round ${res.status}`);
                const data = await res.json();
                if (!cancelled && Array.isArray(data)) setRows(data);
            } catch (e) { console.error('collected-in-round load failed', e); }
        })();
        return () => { cancelled = true; };
    }, [reportCode]);

    if (rows.length === 0) return null;
    const total = rows.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    return (
        <div className="btc-section" data-testid="round-collected-debts">
            <h3>เก็บค้างเก่าในรอบนี้ (นับเข้า "ต้องส่ง")</h3>
            <table className="btc-table">
                <thead>
                    <tr><th>ร้าน</th><th>วันที่บิล</th><th>ยอด</th><th>เก็บเมื่อ</th></tr>
                </thead>
                <tbody>
                    {rows.map((d, i) => (
                        <tr key={d.id ?? i}>
                            <td style={{ textAlign: 'left' }}>{d.customerId ? <CustomerLink name={d.customerName} code={d.customerId} /> : (d.customerName || '-')}</td>
                            <td style={{ textAlign: 'center' }}>{d.billDate || '-'}</td>
                            <td style={{ textAlign: 'right' }}>{money(d.amount)}</td>
                            <td style={{ textAlign: 'center' }}>{d.paidAt ? String(d.paidAt).replace('T', ' ').slice(0, 16) : '-'}</td>
                        </tr>
                    ))}
                    <tr>
                        <td colSpan="2" style={{ textAlign: 'right', fontWeight: 'bold' }}>รวมเก็บค้างเก่า ({rows.length} บิล) → ต้องส่งเพิ่ม:</td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{money(total)}</td>
                        <td></td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

// ค้างจ่ายของรอบนี้ — Vector 2026-10-05: "ให้รายงานอยู่ในกะด้วย". Same endpoint/flag text as /debt-management;
// the server filters by reportCode, we re-check it (and OUTSTANDING) so a loose response can't leak other rounds.
// Best-effort: a failure shows nothing and never breaks the report.
const CREDIT_FLAG_TEXT = {
    NOT_ALLOWED_AT_RECEIPT: '⚠ ร้านนี้ไม่ได้รับอนุญาตให้ค้างตอนรับบิล (ขาจร/ปิดสิทธิ์แล้ว) — โปรดตรวจ',
    UNVERIFIED: '⚠ ตรวจสิทธิ์ค้างของร้านนี้ไม่ได้ตอนสร้างรายการ — โปรดตรวจ',
};
const money = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CreditDebtSection = ({ reportCode }) => {
    const [debts, setDebts] = useState([]);
    useEffect(() => {
        let cancelled = false;
        setDebts([]);
        (async () => {
            try {
                const token = JSON.parse(localStorage.getItem('user') || 'null')?.token;
                if (!token || !reportCode) return;
                const res = await fetch(`${API_BASE_URL}/saledata/api/module/credit-debts?status=OUTSTANDING&reportCode=${encodeURIComponent(reportCode)}`, {
                    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`credit-debts ${res.status}`);
                const data = await res.json();
                if (!cancelled && Array.isArray(data)) {
                    setDebts(data.filter(d => d && d.reportCode === reportCode && (!d.status || d.status === 'OUTSTANDING')));
                }
            } catch (e) { console.error('credit-debts (round) load failed', e); }
        })();
        return () => { cancelled = true; };
    }, [reportCode]);

    if (debts.length === 0) return null;
    const total = debts.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const notAllowed = debts.filter(d => d.flag === 'NOT_ALLOWED_AT_RECEIPT').length;
    return (
        <div className="btc-section" data-testid="round-credit-debts">
            <h3>ค้างจ่ายของรอบนี้</h3>
            {notAllowed > 0 && (
                <div role="alert" style={{ background: '#fff3cd', border: '2px solid #e8590c', color: '#b45309', borderRadius: 6, padding: '8px 12px', marginBottom: 8, fontSize: 14, fontWeight: 700 }}>
                    ⚠ มี {notAllowed} บิลค้างจากร้านที่ไม่ได้อนุญาตให้ค้าง
                </div>
            )}
            <table className="btc-table">
                <thead>
                    <tr><th>ร้าน</th><th>ยอด</th><th>สถานะ</th></tr>
                </thead>
                <tbody>
                    {debts.map((d, i) => (
                        <tr key={d.id ?? i} style={d.flag ? { background: '#fff3cd' } : undefined}>
                            <td style={{ textAlign: 'left' }}>{d.customerId ? <CustomerLink name={d.customerName} code={d.customerId} /> : (d.customerName || '-')}</td>
                            <td style={{ textAlign: 'right' }}>{money(d.amount)}</td>
                            <td style={{ textAlign: 'left' }}>
                                ค้างจ่าย
                                {d.flag && <div style={{ color: '#b45309', fontSize: 11, fontWeight: 700 }}>{CREDIT_FLAG_TEXT[d.flag] || `⚠ ${d.flag} — โปรดตรวจ`}</div>}
                            </td>
                        </tr>
                    ))}
                    <tr>
                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>รวมค้าง ({debts.length} บิล):</td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{money(total)}</td>
                        <td></td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

// ร้านที่คนขับไม่ได้ลงสถานะ — Vector 2026-10-06: "เอาไว้แจ้งเตือนเฉยๆ เพราะไม่กด รายงานเป็นการละเลยหน้าที่".
// Same list as the IMD alert box (seller computes it once: routine shops with no status, minus shops billed this round).
// Alert only: 0 shops → nothing shown; a failure shows one small line and never breaks the report.
const UnrecordedShopsAlert = ({ reportCode }) => {
    const [shops, setShops] = useState(null);
    const [failed, setFailed] = useState(false);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        let cancelled = false;
        setShops(null);
        setFailed(false);
        (async () => {
            try {
                const token = JSON.parse(localStorage.getItem('user') || 'null')?.token;
                if (!token || !reportCode) return;
                const res = await fetch(`${API_BASE_URL}/seller/api/tablet/report/web/rounds/${encodeURIComponent(reportCode)}/unrecorded-customers`, {
                    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`unrecorded-customers ${res.status}`);
                const data = await res.json();
                if (!cancelled && Array.isArray(data)) setShops(data.filter(r => r && r.customerId));
            } catch (e) {
                console.error('unrecorded-customers load failed', e);
                if (!cancelled) setFailed(true);
            }
        })();
        return () => { cancelled = true; };
    }, [reportCode]);

    if (failed) {
        return <p data-testid="unrecorded-shops-failed" style={{ color: '#6c757d', fontSize: 13 }}>ดึงรายการร้านที่คนขับไม่ได้ลงสถานะไม่ได้</p>;
    }
    if (!shops || shops.length === 0) return null;
    return (
        <div className="btc-section" data-testid="round-unrecorded-shops">
            <div role="alert" style={{ background: '#fff3cd', border: '2px solid #e8590c', color: '#b45309', borderRadius: 6, padding: '8px 12px', fontSize: 14, fontWeight: 700 }}>
                ⚠ ร้านที่คนขับไม่ได้ลงสถานะ {shops.length} ร้าน
                <button type="button" onClick={() => setOpen(o => !o)} style={{ marginLeft: 12, fontSize: 13 }}>
                    {open ? 'ซ่อนรายชื่อ' : 'ดูรายชื่อ'}
                </button>
            </div>
            {open && (
                <table className="btc-table">
                    <thead>
                        <tr><th>ร้าน</th></tr>
                    </thead>
                    <tbody>
                        {shops.map(r => (
                            <tr key={r.customerId}>
                                <td style={{ textAlign: 'left' }}><CustomerLink name={r.customerName} code={r.customerId} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

const SummaryTab = ({ fullReport, onReload }) => {
    const [showPromoCustomers, setShowPromoCustomers] = useState(false);
    // Product master (1I/2I → รายละเอียด) for the Sales Summary labels. Failure is silent: the
    // table falls back to the sale lines' own productName.
    const [productMaster, setProductMaster] = useState([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = JSON.parse(localStorage.getItem('user') || 'null')?.token;
                if (!token) return;
                const res = await fetch(`${API_BASE_URL}/seller/api/product-items`, {
                    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled && Array.isArray(data)) setProductMaster(data);
            } catch (e) { /* keep fallback names */ }
        })();
        return () => { cancelled = true; };
    }, []);
    const master = getSection(fullReport, ['master', 'masterReport', 'report', 'masterData'], null);

    return (
        <>
            {renderMasterDetails(master)}

            {(() => {
                // Vector 2026-09-25 11:20 ICT: "ให้มี summary รวมยอด สินค้าแต่ละชนิด" — rendered by
                // hand (same btc-section/btc-table classes as renderTable) so the subtotal row it
                // needs stays local to this table and doesn't touch renderTable's shared contract.
                const { items, totalQuantity, totalPrice } = getItemsData(fullReport);
                const rows = buildSalesSummaryRows(items);
                const title = `ยอดขายรวม (Sales Summary) ${fullReport?.masterReport?.sellerName ? ` ${fullReport.masterReport.sellerName}` : ''}`;
                return (
                    <div className="btc-section">
                        <h3>{title}</h3>
                        {rows.length > 0 ? (
                            <table className="btc-table">
                                <thead>
                                    <tr>
                                        <th>ชื่อสินค้า</th>
                                        <th>ราคาต่อหน่วย</th>
                                        <th>จำนวนรวม</th>
                                        <th>ราคารวม</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.filter((r) => r.type === 'item').map((r, idx) => (
                                        <tr key={`item-${idx}`}>
                                            <td style={{ textAlign: 'left' }}>{productLabel(r.iceCode, r.productName, productMaster)}</td>
                                            <td>{r.unitPrice}</td>
                                            <td>{r.quantity}</td>
                                            <td>{Number(r.total).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ backgroundColor: '#b1becc' }}>
                                        <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>รวมทั้งสิ้น:</td>
                                        <td style={{ fontWeight: 'bold' }}>{totalQuantity}</td>
                                        <td style={{ fontWeight: 'bold' }}>{Number(totalPrice).toLocaleString()}</td>
                                    </tr>
                                    {/* Vector 2026-09-25 11:35: "เอาไปกองไว้หลังรวม เว้น 1 ช่อง" — per-product
                                        subtotals sit together below the grand total, one blank row apart */}
                                    <tr style={{ backgroundColor: 'transparent' }}>
                                        <td colSpan={4} style={{ border: 'none', height: '16px' }}></td>
                                    </tr>
                                    {rows.filter((r) => r.type === 'subtotal').map((r, idx) => (
                                        <tr key={`subtotal-${idx}`} style={{ fontWeight: 'bold', backgroundColor: '#f1f3f5' }}>
                                            <td colSpan={2} style={{ textAlign: 'left' }}>รวม {productLabel(r.iceCode, r.productName, productMaster)}</td>
                                            <td>{r.quantity}</td>
                                            <td>{Number(r.total).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p>ไม่มีข้อมูล</p>
                        )}
                    </div>
                );
            })()}

            {renderTable(
                `การชำระเงิน (Payments) ${fullReport?.masterReport?.sellerName ? ` ${fullReport.masterReport.sellerName}` : ''}`,
                asArray(getSection(fullReport, ['payments', 'paymentData', 'payment', 'paymentList'], [])),
                [
                    { key: 'paymentCode', label: 'วิธีชำระเงิน', format: (v) => paymentCodeLabel(v) },
                    { key: 'amount', label: 'จำนวน' },
                    { key: 'total', label: 'รวม', format: (v) => Number(v).toLocaleString() },
                    { key: 'note', label: 'หมายเหตุ' },
                ],
                'ไม่มีข้อมูล',
                <tr>
                    <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>รวมรับชำระ:</td>
                    <td style={{ fontWeight: 'bold' }}>{Number(asArray(getSection(fullReport, ['payments', 'paymentData', 'payment', 'paymentList'], [])).reduce((s, x) => s + (Number(x?.total) || 0), 0)).toLocaleString()}</td>
                    <td></td>
                </tr>
            )}

            {(() => {
                const loadOuts = asArray(getSection(fullReport, ['loadOutForRound'], []));
                // Group A: entered once, the morning before — only ever real on the seller's
                // lowest round of the day. Not rendered at all elsewhere (not "ไม่มีข้อมูล").
                // Gated on loadOutEligibleRound, NOT array length — this round can be the
                // eligible one with zero load-outs recorded yet, which must still show
                // "ไม่มีข้อมูล" rather than disappear (array-length alone can't tell those apart).
                if (!fullReport?.loadOutEligibleRound) return null;
                return renderTable(
                    'ขึ้นกระสอบ (ออกรอบแรก)',
                    loadOuts,
                    [
                        { key: 'tripNo', label: 'ต๊ง' },
                        { key: 'vehiclePlate', label: 'ทะเบียนรถ' },
                        { key: 'tubeQty', label: 'หลอดใหญ่' },
                        { key: 'crushedQty', label: 'บด' },
                    ],
                    'ไม่มีข้อมูล',
                    <tr>
                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>รวม:</td>
                        <td></td>
                        <td style={{ fontWeight: 'bold' }}>{loadOuts.reduce((s, x) => s + (Number(x?.tubeQty) || 0), 0)}</td>
                        <td style={{ fontWeight: 'bold' }}>{loadOuts.reduce((s, x) => s + (Number(x?.crushedQty) || 0), 0)}</td>
                    </tr>
                );
            })()}

            {/* ป้ายนี้เคยเขียนว่า "เติมระหว่างทาง" ซึ่ง **ผิด** — ข้อมูลที่มันแสดงคือ bag-refill
                = การขับกลับโรงงานเอากระสอบใหม่ขึ้นรถ ซึ่ง Vector เคาะคำว่า **"เพิ่มน้ำแข็ง"** (2026-09-21)
                ส่วน **"เติม"** คือการใช้น้ำแข็งเอง 1 ถุงเติมถุงที่ละลายให้เต็มก่อนส่ง ซึ่งอยู่คนละตาราง
                สองอย่างนี้ทิศตรงข้ามกัน การเรียกด้วยคำเดียวกันคือต้นเหตุของความสับสนทั้งหมดตั้งแต่แรก */}
            {renderTable(
                'เพิ่มน้ำแข็ง (กลับไปเอาที่โรงงาน)',
                asArray(getSection(fullReport, ['refillForRound'], [])),
                [
                    { key: 'seqNo', label: 'ครั้งที่' },
                    { key: 'createdAt', label: 'เวลา', format: (v) => formatHHmm(v) },
                    { key: 'vehiclePlate', label: 'ทะเบียนรถ' },
                    { key: 'tubeQty', label: 'หลอดใหญ่' },
                    { key: 'crushedQty', label: 'บด' },
                ],
                'ไม่มีข้อมูล',
                <tr>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>รวม:</td>
                    <td></td>
                    <td></td>
                    <td style={{ fontWeight: 'bold' }}>{asArray(getSection(fullReport, ['refillForRound'], [])).reduce((s, x) => s + (Number(x?.tubeQty) || 0), 0)}</td>
                    <td style={{ fontWeight: 'bold' }}>{asArray(getSection(fullReport, ['refillForRound'], [])).reduce((s, x) => s + (Number(x?.crushedQty) || 0), 0)}</td>
                </tr>
            )}
            {Number(fullReport?.refillUnassignedCount) > 0 && (
                <p style={{ color: '#6c757d', fontSize: '13px' }}>
                    มีอีก {fullReport.refillUnassignedCount} ครั้งที่ระบุรอบไม่ได้
                </p>
            )}

            {/* เงินที่ IMD ส่งมา — every round has its own money submission, so this is not gated on
                bagReturnEligibleRound. Vector 2026-09-27: บัญชีปลดล็อก/แก้ยอดจากหน้านี้ */}
            <ImdMoneySection reportCode={fullReport?.masterReport?.reportCode ?? master?.reportCode} />

            <CreditDebtSection reportCode={fullReport?.masterReport?.reportCode ?? master?.reportCode} />
            <CollectedDebtSection reportCode={fullReport?.masterReport?.reportCode ?? master?.reportCode} />

            <UnrecordedShopsAlert reportCode={fullReport?.masterReport?.reportCode ?? master?.reportCode} />

            {/* Group C: shift-closing checkpoint — only real on the last round of the กะ (or the
                seller's highest round of the day for rounds without one). Gated on
                bagReturnEligibleRound (see the load-out comment above for why array length alone
                isn't enough). */}
            {fullReport?.bagReturnEligibleRound && (
                <BagReturnSection fullReport={fullReport} onReload={onReload} />
            )}

            {/* ตาราง "สินค้าติดรถ (Cargo)" ถูกลบ 2026-09-21 — มันอ่าน `cargos` ซึ่งเป็นตารางเก่า
                ที่ไม่มีใครเขียนลงแล้ว (2 แถวทั้งระบบ ไม่ผูก reportCode) จึงขึ้น "ไม่มีข้อมูล" ตลอด
                ของจริงคือตาราง **ขึ้นกระสอบ (ออกรอบแรก)** ข้างบน ซึ่งอ่าน loadOutForRound และทำงานอยู่
                Vector ยืนยันเองว่า "สินค้าติดรถ (Cargo) = ยอดขึ้นกระสอบ" ⇒ เป็นของเดียวกัน ไม่ใช่สองอย่าง
                ปล่อยตารางว่างไว้มีแต่ทำให้คนอ่านคิดว่าข้อมูลหาย */}

            {/* สรุปถุง — เลิกอ่าน `bagSummaries` (ตารางเก่าที่ว่างเปล่าทั้งตาราง 0 แถว)
                เปลี่ยนมาอ่าน `bagVarianceForRound` ที่หลังบ้านคำนวณให้ 2026-09-21
                โชว์เฉพาะรอบปิดกะ เพราะกระสอบกลับมาครั้งเดียวตอนจบกะ ไม่ได้กลับทุกรอบ
                ถ้าแปะทุกรอบ คนอ่านจะเห็นเลขเดียวกันซ้ำแล้วนึกว่าขาดหลายเท่า */}
            {(() => {
                const bv = fullReport?.bagVarianceForRound;
                if (!bv) return null;
                // variance is forced to 0 by the backend until someone logs กระสอบกลับ — that 0 is not "ครบพอดี"
                const fmt = (n) => {
                    if (!bv.bagsReturnRecorded) return <span style={{ color: '#888' }}>ยังไม่ลงกระสอบกลับ</span>;
                    const v = Number(n) || 0;
                    if (v === 0) return <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>ครบพอดี</span>;
                    return (
                        <span style={{ color: v < 0 ? '#c62828' : '#ef6c00', fontWeight: 'bold' }}>
                            {v < 0 ? `ขาด ${Math.abs(v)}` : `เกิน ${v}`}
                        </span>
                    );
                };
                // ทุกคอลัมน์ตั้งให้อ่านแล้วบวกลบตามกันได้: ขาย + เติม = รวมที่ใช้ไป · ขาด/เกิน = ได้คืน − รวมที่ใช้ไป
                // ได้คืน = จบกะ + เก็บกลับระหว่างทาง (2 ทางตาม BagLedgerSummaryService)
                const num = (v) => <span style={{ whiteSpace: 'nowrap' }}>{Number(v) || 0}</span>;
                // แยกทางได้เฉพาะตอนหลังบ้านส่งองค์ประกอบมา — ไม่มีก็โชว์แค่ยอดรวม ไม่เดาเป็น 0
                const back = (v, it) => (
                    <>
                        <span style={{ whiteSpace: 'nowrap' }}>{Number(v) || 0}</span>
                        {(it.returned != null || it.collected != null) && (
                            <div style={{ fontSize: '12px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                                จบกะ {Number(it.returned) || 0} + เก็บระหว่างทาง {Number(it.collected) || 0}
                            </div>
                        )}
                    </>
                );
                // กินทั้งแถวของ grid 2 คอลัมน์ (index.js) — ไม่งั้นบนแท็บเล็ตการ์ดถูกบีบเหลือ ~250px
                return (
                    <div style={{ gridColumn: '1 / -1' }}>
                        {renderTable(
                            `สรุปถุง — กระสอบขาด/เกิน ${fullReport?.masterReport?.sellerName ? ` ${fullReport.masterReport.sellerName}` : ''}`,
                            [
                                { kind: 'หลอด', sold: bv.soldTube, melt: bv.meltTube, used: (Number(bv.soldTube) || 0) + (Number(bv.meltTube) || 0), back: bv.bagsBackTube, returned: bv.returnedTube, collected: bv.collectedTube, diff: bv.varianceTube },
                                { kind: 'บด', sold: bv.soldCrushed, melt: bv.meltCrushed, used: (Number(bv.soldCrushed) || 0) + (Number(bv.meltCrushed) || 0), back: bv.bagsBackCrushed, returned: bv.returnedCrushed, collected: bv.collectedCrushed, diff: bv.varianceCrushed },
                            ],
                            [
                                { key: 'kind', label: 'ชนิด' },
                                { key: 'sold', label: 'ขายไป', format: num },
                                { key: 'melt', label: 'เติม', format: num },
                                { key: 'used', label: 'รวมที่ใช้ไป', format: num },
                                { key: 'back', label: 'ได้คืน', format: back },
                                { key: 'diff', label: 'ขาด/เกิน', format: (v) => fmt(v) },
                            ],
                            'ไม่มีข้อมูล',
                            null,
                            'ขาด/เกิน = ได้คืน − (ขาย + เติม) · เติม = ใช้น้ำแข็งเติมถุงลูกค้าที่ละลาย กระสอบเปล่ายังกลับมาที่รถ'
                        )}
                        <BagEntriesSection reportCode={fullReport?.masterReport?.reportCode} onReload={onReload} />
                    </div>
                );
            })()}

            {(() => {
                const { items, totalQuantity, totalPrice } = getBagSalesData(fullReport);
                return renderTable(
                    `ยอดขายถุง (Bag Sales) ${fullReport?.masterReport?.sellerName ? ` ${fullReport.masterReport.sellerName}` : ''}`,
                    items,
                    [
                        { key: 'displayProductName', label: 'ชื่อถุง' },
                        { key: 'unitPrice', label: 'ราคาต่อหน่วย' },
                        { key: 'quantity', label: 'จำนวน' },
                        { key: 'total', label: 'รวม', format: (v) => Number(v).toLocaleString() },
                    ],
                    'ไม่มีข้อมูล',
                    <tr>
                        <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>รวมทั้งสิ้น:</td>
                        <td style={{ fontWeight: 'bold' }}>{totalQuantity}</td>
                        <td style={{ fontWeight: 'bold' }}>{Number(totalPrice).toLocaleString()}</td>
                    </tr>
                );
            })()}

            {renderTable(
                `เติมน้ำแข็ง ${fullReport?.masterReport?.sellerName ? ` ${fullReport.masterReport.sellerName}` : ''}`,
                asArray(getSection(fullReport, ['meltCompensations', 'refills', 'refillData'], [])),
                [
                    { key: 'refillDateTime', label: 'เวลา', format: (val) => val ? new Intl.DateTimeFormat('th-TH', { hour: '2-digit', minute: '2-digit' }).format(new Date(val)) : '' },
                    { key: 'productName', label: 'ชื่อสินค้า' },
                    { key: 'quantity', label: 'จำนวน' },
                ],
                'ไม่มีข้อมูล',
                <tr>
                    <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>รวม:</td>
                    <td style={{ fontWeight: 'bold' }}>{asArray(getSection(fullReport, ['meltCompensations', 'refills', 'refillData'], [])).reduce((s, x) => s + (Number(x?.quantity) || 0), 0)}</td>
                </tr>
            )}

            {/* ตาราง "คืนถุง (Bag Return)" ถูกลบ 2026-09-21 — อ่าน `bagReturns` ซึ่งเป็นตารางเก่า
                ที่ว่างเปล่าทั้งตาราง (0 แถว) ของจริงคือ **กระสอบกลับ** ข้างบน ที่อ่าน bagReturnForRound
                Vector ยืนยันเองว่า "คืนถุง (Bag Return) = ยอดที่ได้ถุงคืน" ⇒ เป็นของเดียวกัน */}

            {(() => {
                const promo = getSection(fullReport, ['promotions', 'promotion'], { totalUsed: 0, usages: [] });
                const usages = asArray(promo?.usages || []);
                // จำนวนการใช้จริง = times × max(1, floor(ถุงในชื่อโปร ÷ 100))
                // bags < 100 นับเป็น 1 (มีการใช้โปรโมชัน ไม่ควรเป็น 0)
                // เช่น PR_3P50B → max(1, floor(50/100)) = 1; PR_6P200B → 2
                const extractBagMultiplier = (code) => {
                    const m = code && String(code).match(/(\d+)B/i);
                    return m ? Math.max(1, Math.floor(Number(m[1]) / 100)) : 1;
                };
                const totalUsageCount = usages.reduce((sum, u) => {
                    const code = u?.promoCode ?? u?.code ?? u?.name ?? u?.promotionCode ?? '';
                    const times = u?.times ?? u?.count ?? u?.usageCount ?? 0;
                    return sum + Number(times) * extractBagMultiplier(code);
                }, 0);
                const promoCustomerRows = usages.flatMap((u) => {
                    const code = u?.promoCode ?? u?.code ?? u?.name ?? u?.promotionCode ?? 'ไม่ระบุ';
                    return asArray(u?.customers).map((c) => ({
                        code,
                        customerId: c?.customerId ?? '',
                        customerName: c?.customerName ?? '',
                    }));
                });
                return (
                    <div className="btc-section">
                        <h3>โปรโมชัน {fullReport?.masterReport?.sellerName ? ` ${fullReport.masterReport.sellerName}` : ''}</h3>
                        <p><strong>ใช้งานรวม:</strong> {promo?.totalUsed ?? 0}</p>
                        {usages.length > 0 ? (
                            <ul>
                                {usages.map((u, i) => {
                                    const code = u?.promoCode ?? u?.code ?? u?.name ?? u?.promotionCode ?? 'ไม่ระบุ';
                                    const times = (u?.times ?? u?.count ?? u?.usageCount ?? 0);
                                    const usageCount = Number(times) * extractBagMultiplier(code);
                                    const customersCount = Array.isArray(u?.customers) ? u.customers.length : (u?.customerCount ?? 0);
                                    // forgotTimes = ส่วนของ times ที่เป็นโปรลืมส่ง (นับรวมอยู่ใน times แล้ว ไม่ใช่นับเพิ่ม)
                                    const forgotTimes = Number(u?.forgotTimes) || 0;
                                    return (
                                        <li key={i}>
                                            {code} - {times} ครั้ง{customersCount ? ` (${customersCount} ลูกค้า)` : ''}{' '}
                                            <span style={{ color: '#007bff' }}>(จำนวนการใช้: {usageCount})</span>
                                            {forgotTimes > 0 && <span style={{ color: '#ef6c00' }}> · ลืมส่ง {forgotTimes}</span>}
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p>ยังไม่มีรายละเอียดโปรโมชัน</p>
                        )}
                        {usages.length > 0 && (
                            <div style={{ marginTop: '12px', padding: '10px', background: '#f8f9fa', borderRadius: '6px', borderLeft: '3px solid #007bff' }}>
                                <p style={{ margin: '0 0 4px 0' }}><strong>จำนวนการใช้</strong> {totalUsageCount}</p>
                                <p style={{ margin: 0 }}><strong>{totalUsageCount} x 5 = {totalUsageCount * 5}</strong></p>
                            </div>
                        )}
                        {/* Vector 2026-09-25 11:37: "ขอปุ่มดูลูกค้าใช้โปรโมชั่นทั้งหมด" — the backend already
                            sends usages[].customers ({customerId, customerName}, unique per promo);
                            this page only used it for the "(N ลูกค้า)" count until now. */}
                        {promoCustomerRows.length > 0 && (
                            <div style={{ marginTop: '12px' }}>
                                <button onClick={() => setShowPromoCustomers((v) => !v)}>
                                    {showPromoCustomers ? 'ซ่อนลูกค้าที่ใช้โปรโมชัน' : `ดูลูกค้าที่ใช้โปรโมชันทั้งหมด (${promoCustomerRows.length})`}
                                </button>
                                {showPromoCustomers && (
                                    <table className="btc-table" style={{ marginTop: '8px' }}>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>โปรโมชัน</th>
                                                <th>ชื่อลูกค้า</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {promoCustomerRows.map((r, idx) => (
                                                <tr key={`${r.code}-${r.customerId}-${idx}`}>
                                                    <td>{idx + 1}</td>
                                                    <td>{r.code}</td>
                                                    <td><CustomerLink name={r.customerName} code={r.customerId} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}
                    </div>
                );
            })()}
        </>
    );
};

export default SummaryTab;
